import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mic, Sparkles, Clock, PhoneCall, Video, Code2, Mail, FileCheck2 } from "lucide-react";
import { Window } from "@/components/public/home/mocks";

const STAGES = [
  { l: "Invited", Icon: Mail, tone: "bg-slate-100 text-slate-600 border-slate-200" },
  { l: "Phone call", Icon: PhoneCall, tone: "bg-emerald-50 text-emerald-700 border-emerald-200", live: true },
  { l: "Video", Icon: Video, tone: "bg-cyan-50 text-cyan-700 border-cyan-200", live: true },
  { l: "Coding", Icon: Code2, tone: "bg-indigo-50 text-indigo-700 border-indigo-200", live: true },
  { l: "Scorecard", Icon: FileCheck2, tone: "bg-violet-50 text-violet-700 border-violet-200" },
];
const PEOPLE = [
  { n: "Priya Raman", r: "Java Developer", s: 86, o: 4 },
  { n: "Marcus Vale", r: "DevOps Engineer", s: 79, o: 3 },
  { n: "Lena Kowalski", r: "Data Engineer", s: 91, o: 1 },
  { n: "Omar Siddiq", r: "QA Automation", s: 74, o: 2 },
];
const LINES = [
  ["AI", "Hi {n}, this is Ava, an AI interviewer. Is now a good time for a quick screening call?"],
  ["C", "Yes, now works."],
  ["AI", "Great. Walk me through a recent project you're most proud of."],
  ["C", "Sure — last quarter I led the migration of our nightly jobs to an event-driven pipeline…"],
];
const TICK = 2200;
const CYCLE = STAGES.length + 1;
const verdict = (s) => (s >= 85 ? "Strong hire" : s >= 75 ? "Hire" : "Maybe");

function useTick() {
  const [t, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT((x) => x + 1), TICK); return () => clearInterval(id); }, []);
  return t;
}

function CountUp({ to }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf, start;
    const step = (ts) => { start ??= ts; const p = Math.min(1, (ts - start) / 900); setV(Math.round(to * p)); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return <>{v}</>;
}

function Typing({ name, live }) {
  const [i, setI] = useState(0);
  const [c, setC] = useState(0);
  useEffect(() => {
    const full = LINES[i][1].replace("{n}", name.split(" ")[0]);
    const id = setTimeout(() => (c < full.length ? setC(c + 1) : (setI((i + 1) % LINES.length), setC(0))), c < full.length ? 28 : 1300);
    return () => clearTimeout(id);
  }, [c, i]);
  const [who, raw] = LINES[i];
  const text = raw.replace("{n}", name.split(" ")[0]);
  return (
    <div className="rounded-xl bg-[#0E1422] px-3.5 py-3 text-xs leading-relaxed min-h-[64px]" data-testid="hero-live-transcript">
      <div className="flex items-center gap-2 text-[10px] font-mono text-emerald-300 mb-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} /> {live ? "LIVE CALL" : "CALL TRANSCRIPT"} · {name}
        <span className="ml-auto flex items-end gap-0.5 h-3">{[0, 1, 2, 3].map((k) => <span key={k} className="w-0.5 bg-emerald-400 rounded-full rs-wave" style={{ animationDelay: `${k * 0.12}s` }} />)}</span>
      </div>
      <span className={who === "AI" ? "text-indigo-300 font-semibold" : "text-white font-semibold"}>{who === "AI" ? "Ava (AI)" : name.split(" ")[0]}:</span>{" "}
      <span className="text-slate-300">{text.slice(0, c)}</span><span className="inline-block w-1.5 h-3 bg-slate-400 ml-0.5 align-middle animate-pulse" />
    </div>
  );
}

function Row({ p, stage }) {
  const S = STAGES[Math.min(stage, STAGES.length - 1)];
  const scored = stage >= STAGES.length - 1;
  return (
    <div className="flex items-center gap-3 py-2.5" data-testid="hero-pipeline-row">
      <div className="w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center text-[11px] font-semibold shrink-0">{p.n.split(" ").map((x) => x[0]).join("")}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{p.n}</div>
        <div className="mt-1 flex gap-0.5">{STAGES.map((_, k) => <span key={k} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${k <= stage ? "bg-gradient-to-r from-indigo-500 to-cyan-500" : "bg-slate-100"}`} />)}</div>
      </div>
      <AnimatePresence mode="wait">
        <motion.span key={S.l} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}
          className={`shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium ${S.tone}`}>
          {S.live && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}<S.Icon className="w-3 h-3" />{scored ? verdict(p.s) : S.l}
        </motion.span>
      </AnimatePresence>
      <span className="w-8 text-right font-mono text-sm font-semibold text-slate-900">{scored ? <CountUp to={p.s} /> : <span className="text-slate-300">—</span>}</span>
    </div>
  );
}

export function LivePipeline() {
  const t = useTick();
  const stages = PEOPLE.map((p) => (t + p.o) % CYCLE);
  const vis = stages.map((s) => Math.min(s, STAGES.length - 1));
  const base = useRef({ inv: 44, scr: 18, done: 15 }).current;
  const caller = useRef(PEOPLE[2].n);
  const onCall = PEOPLE.find((_, i) => vis[i] === 1);
  if (onCall) caller.current = onCall.n;
  const running = vis.filter((s) => s > 0 && s < 4).length;
  const scoredIdx = vis.map((s, i) => [s, i]).filter(([s]) => s === STAGES.length - 1).map(([, i]) => i);
  const latest = PEOPLE[scoredIdx[scoredIdx.length - 1] ?? 0];
  const tiles = [["Invited", base.inv + PEOPLE.length], ["Screening", base.scr + vis.filter((s) => s > 0 && s < 4).length], ["Completed", base.done + scoredIdx.length], ["Hired", 3]];

  return (
    <div className="relative" data-testid="hero-visual">
      <div className="absolute -inset-6 bg-gradient-to-tr from-indigo-200/50 via-cyan-100/40 to-transparent blur-3xl rounded-full" />
      <Window dark={false} title="recruitsift.com · Senior Data Engineer · live" className="relative">
        <div className="p-4 sm:p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            {tiles.map(([l, v]) => (
              <div key={l} className="rounded-xl border border-slate-100 bg-slate-50/70 p-2.5" data-testid={`hero-tile-${l.toLowerCase()}`}>
                <div className="text-[10px] text-slate-500">{l}</div>
                <AnimatePresence mode="popLayout"><motion.div key={v} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="font-display text-xl font-semibold text-slate-900">{v}</motion.div></AnimatePresence>
              </div>
            ))}
          </div>
          <div className="divide-y divide-slate-100">{PEOPLE.map((p, i) => <Row key={p.n} p={p} stage={vis[i]} />)}</div>
          <div className="mt-3"><Typing key={caller.current} name={caller.current} live={!!onCall} /></div>
        </div>
      </Window>
      <div className="hidden sm:flex absolute -left-8 -bottom-8 items-center gap-2.5 rounded-2xl bg-white border border-slate-200 shadow-xl px-3.5 py-2.5 rs-float">
        <div className="w-8 h-8 rounded-full bg-emerald-50 grid place-items-center"><Mic className="w-4 h-4 text-emerald-600" /></div>
        <div><div className="text-xs font-semibold text-slate-900">{running} interview{running === 1 ? "" : "s"} running</div><div className="text-[10px] text-slate-500 font-mono">voice · video · coding</div></div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={latest.n} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
          className="hidden sm:flex absolute -right-6 -top-5 items-center gap-2.5 rounded-2xl bg-[#0E1422] text-white shadow-xl px-3.5 py-2.5" data-testid="hero-scorecard-toast">
          <Sparkles className="w-4 h-4 text-cyan-300" />
          <div><div className="text-xs font-semibold">Scorecard ready</div><div className="text-[10px] text-slate-400 font-mono">{latest.n} · {verdict(latest.s)} · {latest.s}</div></div>
        </motion.div>
      </AnimatePresence>
      <div className="hidden lg:flex absolute right-10 -bottom-5 items-center gap-2 rounded-full bg-white border border-slate-200 shadow-lg px-3 py-1.5 text-[11px] text-slate-600 rs-float" style={{ animationDelay: "2s" }}>
        <Clock className="w-3.5 h-3.5 text-indigo-500" /> 24/7 · candidate's timezone
      </div>
    </div>
  );
}
